import React, { useState, useEffect, useContext, useCallback } from 'react';
import { UserContext } from '../context/UserContext';
import { platformRulesApi } from '../api/platformRules';
import HrnbContainer, { HrnbRow, HrnbCol } from '../components/controls/HrnbContainer';
import HrnbCard from '../components/controls/HrnbCard';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbLoader from '../components/controls/HrnbLoader';
import { HrnbTabs, HrnbTab } from '../components/controls/HrnbTabs';
import '../css/pages/RulesPage.css';

/**
 * Страница правил платформы
 * Интеграция: platformRulesApi.listPublic() / platformRulesApi.list()
 *
 * Категории (табы):
 *  - all — Все правила
 *  - COMMUNITY_GUIDELINES — Правила сообщества
 *  - TERMS_OF_SERVICE — Условия использования
 *  - CONTENT_POLICY — Политика контента
 *  - PRIVACY_POLICY — Политика конфиденциальности
 *  - CODE_OF_CONDUCT — Кодекс поведения
 */
const CATEGORY_TABS = [
    { value: 'all', label: 'Все правила' },
    { value: 'TERMS_OF_SERVICE', label: 'Условия использования' },
    { value: 'CODE_OF_CONDUCT', label: 'Кодекс поведения' },
    { value: 'CONTENT_POLICY', label: 'Политика контента' },
    { value: 'PRIVACY_POLICY', label: 'Политика конфиденциальности' },
    { value: 'COMMUNITY_GUIDELINES', label: 'Правила сообщества' },
];


export default function RulesPage() {
    const { currentUser } = useContext(UserContext);
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');

    const fetchRules = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                size: 100,
                sortBy: 'sortOrder',
                direction: 'ASC',
            };
            // Authorised users see all rules, guests see only active ones
            const data = currentUser
                ? await platformRulesApi.list(params)
                : await platformRulesApi.listPublic(params);
            const rulesList = data?.content || [];
            setRules(rulesList);
        } catch (err) {
            setError(err.message || 'Ошибка при загрузке правил');
            setRules([]);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    // Filter by selected category (client-side, since we load everything at once)
    const visibleRules = activeTab === 'all'
        ? rules
        : rules.filter(r => r.category === activeTab);

    const showInactiveNotice = currentUser && rules.some(r => !r.isActive);

    return (
        <HrnbContainer className="rules-page">
            <HrnbRow>
                <HrnbCol col={12}>
                    <div className="rules-page__header">
                        <h1 className="rules-page__title">Правила платформы</h1>
                        <p className="rules-page__description">
                            Ознакомьтесь с правилами сообщества, условиями использования,
                            политикой контента и другими документами платформы.
                        </p>
                    </div>
                </HrnbCol>
            </HrnbRow>

            <HrnbRow>
                <HrnbCol col={12}>
                    <HrnbTabs value={activeTab} onChange={setActiveTab} className="rules-page__tabs">
                        {CATEGORY_TABS.map(tab => (
                            <HrnbTab key={tab.value} label={tab.label} value={tab.value} />
                        ))}
                    </HrnbTabs>
                </HrnbCol>
            </HrnbRow>

            <HrnbRow>
                <HrnbCol col={12}>
                    {loading && (
                        <div className="rules-page__loading">
                            <HrnbLoader />
                        </div>
                    )}

                    {error && (
                        <HrnbAlert type="danger">{error}</HrnbAlert>
                    )}

                    {!loading && !error && visibleRules.length === 0 && (
                        <HrnbCard className="rules-page__empty">
                            <p>В этой категории правила пока не добавлены.</p>
                        </HrnbCard>
                    )}

                    {!loading && !error && showInactiveNotice && (
                        <div className="rules-page__notice">
                            Отображаются все правила, включая неактивные (доступно авторизованным пользователям).
                        </div>
                    )}

                    {!loading && !error && visibleRules.length > 0 && (
                        <div className="rules-page__list">
                            {visibleRules.map((rule, idx) => (
                                <RuleItem key={rule.id} rule={rule} index={idx} />
                            ))}
                        </div>
                    )}
                </HrnbCol>
            </HrnbRow>
        </HrnbContainer>
    );
}

/**
 * Single platform rule card
 */
function RuleItem({ rule, index }) {
    const { title, text, isActive } = rule;

    return (
        <HrnbCard
            className={`rule-item${isActive === false ? ' rule-item--inactive' : ''}`}
            title={title || `Правило #${index + 1}`}
            actions={
                <span className="rule-item__order">#{index + 1}</span>
            }
        >
            <div className="rule-item__content">
                <p className="rule-item__text">{text}</p>
                {isActive === false && (
                    <div className="rule-item__status">Неактивно</div>
                )}
            </div>
        </HrnbCard>
    );
}
